<template>
  <div>
    <div class="editable-checklist-question">
      <div v-if="editing && editable" class="question-wrap">
        <div v-if="quesNum" class="question-title-wrap edit">
          <div class="left">
            <p class="num">
              {{ quesNum }}
            </p>
          </div>
        </div>
      </div>
      <div v-else class="question-wrap">
        <div
          class="question-title-wrap"
          :class="{ edit: dropDown, active: dropDown }"
          @click="dropDownToggle()"
        >
          <div class="left">
            <div class="title">
              <p v-if="quesNum" class="num">
                {{ quesNum }}
              </p>
            </div>
            <div v-if="!dropDown" class="question-title">
              <p class="question-title_title">
                {{ value.title }}
              </p>
              <p v-if="value.subtitle" class="question-title_subtitle">
                {{ value.subtitle }}
              </p>
              <div v-if="isError && errorMessage.length" class="mt-12">
                <p v-for="(message, index) in errorMessage" :key="index" class="warning">
                  <i class="icon-info" />
                  {{ message }}
                </p>
              </div>
            </div>
          </div>
          <div class="right">
            <contentChip
              v-if="value.focus === 1 && !hideFocus"
              medium-blue
              :text-label="$t('重點關注')"
            />
            <i v-if="dropDown && editable" class="icon-unfold-less" />
            <i v-else-if="!dropDown && editable" class="icon-unfold-more" />
            <i v-else class="icon-drag" />
          </div>
        </div>
        <slide-up-down :active="dropDown" :duration="500">
          <div v-if="isLoading.content || !hasFetch" class="question-container">
            <DotLoading primary />
          </div>
          <div v-if="editable && !isLoading.content && hasFetch" class="question-container">
            <ValidationObserver ref="formObserver" v-slot="{ invalid }">
              <LlValidateWatcher :errors="invalid" @input="$_isError($event)" />
              <InputListCard>
                <div class="input-item">
                  <div class="input-title-wrap">
                    <p>{{ titleAreaTitle }}<span>*</span></p>
                    <p class="gray">{{ titleLength(value.title) }}/300</p>
                  </div>
                  <div class="input">
                    <StateInputButton
                      :value="value.title"
                      text-length="300"
                      placeholder="輸入"
                      rules="required|max:300"
                      @input="$_onInput($event, 'title')"
                    />
                  </div>
                </div>
                <div v-if="!hideToggleFocus" class="input-item">
                  <p class="input-title">{{ $t('重點關注') }}<span>*</span></p>
                  <div class="input">
                    <RadioButton
                      :value="value.focus"
                      is-narrow
                      row
                      :radio-button-item="focusRadio"
                      :radio-name="radioName"
                      @input="$_onInput($event, 'focus')"
                    />
                  </div>
                </div>
                <div v-if="editableQuesType" class="input-item">
                  <p class="input-title">{{ $t('題型') }}<span>*</span></p>
                  <div class="input">
                    <RadioButton
                      :value="value.quesType"
                      is-narrow
                      row
                      tablet-column
                      :radio-button-item="quesTypeRadio"
                      :radio-name="typeRadioName"
                      @input="$_onInput($event, 'quesType')"
                    />
                  </div>
                </div>
                <div v-if="value.quesType === 1" class="range-input">
                  <WsStateRangeInput001
                    class="mt-20"
                    :range-data="value.controlLimitData"
                    :lower-limit="parseFloat(value.specLimitData.lower)"
                    :upper-limit="parseFloat(value.specLimitData.upper)"
                    :lower-data-placeholder="$t('輸入下限')"
                    :upper-data-placeholder="$t('輸入上限')"
                    :custom-messages="controlLimitCustomMessages"
                    :title="$t('Control Limit (管制界線)')"
                    :popup-title="$t('管制界線設定建議')"
                  />
                  <WsStateRangeInput001
                    class="mt-20 mb-20"
                    :range-data="value.specLimitData"
                    :lower-data-placeholder="$t('輸入下限')"
                    :upper-data-placeholder="$t('輸入上限')"
                    :custom-messages="specLimitCustomMessages"
                    :title="$t('Spec Limit (合規界線)')"
                    :popup-title="$t('合規界線設定建議')"
                  />
                </div>
                <div v-if="textLabel" class="input-item">
                  <p class="input-title">{{ textLabel }}<span>*</span></p>
                  <div class="input">
                    <WsInputTextareaButton001
                      :value="value.remark"
                      :min-height="134"
                      :placeholder="textareaPlaceholder"
                      rules="required"
                      @input="$_onInput($event, 'remark')"
                    />
                  </div>
                </div>
                <div v-if="!nonFileUpload" class="input-item">
                  <p class="input-title">
                    {{ attachTitle }}
                  </p>
                  <div class="file-list mb-16">
                    <LvDownloadFileBtn
                      v-for="(attach, attachIndex) in value.attaches"
                      showDeleteBtn
                      :key="`attach_${attachIndex}`"
                      :id="attach.id"
                      :num="attachIndex"
                      :url="attach.url"
                      :name="attach.name"
                      :download="attach.hasScope"
                      :fileType="attach.fileType"
                      :hasScope="attach.hasScope"
                      :isSystem="attach.isSystem"
                      :chipList="attach.chipList"
                      :versionNumber="attach.versionNumber"
                      :module="uploadModule"
                      :showDownloadBtn="attach.showDownloadBtn"
                      :isClickable="attach.isClickable"
                      :isImageViewable="_isImageViewable"
                      class="file"
                      @go-to="$_onGoToFile($event)"
                      @remove="$_onDeleteFile(attachIndex, 'attaches')"
                    />
                  </div>
                  <LvUploadFileBtn
                    multiple
                    :uploadModule="uploadModule"
                    :remindText="`${$t('檔案大小限制')} ≤ 30 MB`"
                    :selectedFileList="value.attaches"
                    @input="$_onInput($event, 'attaches')"
                  />
                </div>
                <div v-if="ocapEdit" class="input-item">
                  <p class="input-title">
                    {{ textLabelOcap }}
                  </p>
                  <div class="input">
                    <WsInputTextareaButton001
                      :value="value.ocapRemark"
                      :min-height="134"
                      :placeholder="$t('輸入')"
                      @input="$_onInput($event, 'ocapRemark')"
                    />
                  </div>
                </div>
                <div v-if="ocapEdit" class="input-item">
                  <p class="input-title">
                    {{ attachTitleOcap }}
                  </p>
                  <div class="file-list">
                    <LvDownloadFileBtn
                      v-for="(ocapAttach, ocapAttachIndex) in editQues.ocapAttaches"
                      showDeleteBtn
                      :key="`ocap_${ocapAttachIndex}`"
                      :id="ocapAttach.id"
                      :num="ocapAttachIndex"
                      :url="ocapAttach.url"
                      :name="ocapAttach.name"
                      :download="ocapAttach.hasScope"
                      :fileType="ocapAttach.fileType"
                      :hasScope="ocapAttach.hasScope"
                      :isSystem="ocapAttach.isSystem"
                      :chipList="ocapAttach.chipList"
                      :versionNumber="ocapAttach.versionNumber"
                      :module="uploadModule"
                      :showDownloadBtn="ocapAttach.showDownloadBtn"
                      :isClickable="ocapAttach.isClickable"
                      :isImageViewable="_isImageViewable"
                      class="file"
                      @go-to="$_onGoToFile($event)"
                      @remove="$_onDeleteFile(ocapAttachIndex, 'ocapAttaches')"
                    />
                  </div>
                  <LvUploadFileBtn
                    multiple
                    :uploadModule="uploadModule"
                    :remindText="`${$t('檔案大小限制')} ≤ 30 MB`"
                    :selectedFileList="editQues.ocapAttaches"
                    @input="$_onInput($event, 'ocapAttaches')"
                  />
                </div>
                <div class="input-item">
                  <div class="input">
                    <LlState
                      type="act-pick"
                      title="關聯法規"
                      :value="value.lawList"
                      @input="$_onInput($event, 'lawList')"
                    />
                  </div>
                </div>
                <div class="input-item">
                  <div class="input">
                    <LvPickGuideline
                      :value="value.guidelineList"
                      title="相關內規"
                      class="mb-20"
                      @input="$_onInput($event, 'guidelineList')"
                    />
                  </div>
                </div>
                <div class="input-item">
                  <div class="input">
                    <LvDropdownMultiCategorySelect
                      :value="value.riskSignal"
                      outline
                      label="風險"
                      title="自訂風險"
                      :menu-items="effectList"
                      :createFields="effect.createFields"
                      :updateFields="effect.updateFields"
                      :scope="_effectScope"
                      :deletePopupContentText="effect.deleteText"
                      :createUrl="_effectCreateUrl"
                      @created="$emit('created')"
                      @delete="$emit('delete-effect', $event)"
                      @update="$emit('update', $event)"
                      @input="$_onInput($event, 'riskSignal')"
                    />
                  </div>
                </div>
                <slot />
              </InputListCard>
            </ValidationObserver>
          </div>
        </slide-up-down>
      </div>
      <div v-if="!editing && editable" class="edit-tool">
        <WsTextButton001
          leading-icon="icon-delete"
          red
          font-size-xs
          no-padding
          @click="deleteQues()"
        >
          {{ $t('刪除') }}
        </WsTextButton001>
        <WsTextButton001
          v-if="!nonCopyBtn"
          leading-icon="icon-copy"
          primary
          font-size-xs
          no-padding
          @click="copyQuesClick()"
        >
          {{ $t('複製') }}
        </WsTextButton001>
      </div>
    </div>
  </div>
</template>
<script>
import S_scope from '@/service/api/v1/scope';
import S_factoryEffect from '@/service/api/v1/factoryEffect';
import H_file from '@/helpers/file';
import Spare_factoryEffect from '@/spare/factoryEffect';

export default {
  name: 'EditableChecklistQuestion002',
  props: {
    textareaPlaceholder: {
      type: String,
      default() {
        return this.$t('輸入');
      },
    },
    nonFileUpload: {
      type: Boolean,
      default: false,
    },
    nonCopyBtn: {
      type: Boolean,
      default: false,
    },
    titleAreaTitle: {
      type: String,
      default() {
        return this.$t('標題');
      },
    },
    hideToggleFocus: {
      type: Boolean,
      default: false,
    },
    ocapEdit: {
      type: Boolean,
      default: false,
    },
    hideFocus: {
      type: Boolean,
      default: false,
    },
    radioName: {
      type: String,
      default: '',
    },
    typeRadioName: {
      type: String,
      default: '',
    },
    select: {
      type: Boolean,
      default: false,
    },
    textLabel: {
      type: String,
      default: '',
    },
    value: {
      type: Object,
      default() {
        return {};
      },
    },
    editable: {
      type: Boolean,
      default: false,
    },
    risk: {
      type: String,
      default: '',
    },
    quesNum: {
      type: Number,
      default: 0,
    },
    uploadImageUrl: {
      type: String,
      default: '',
    },
    uploadFileUrl: {
      type: String,
      default: '',
    },
    factory: {
      type: String,
    },
    subtitle: {
      type: String,
      default: '',
    },
    chapterNum: {
      type: Number,
      default: 0,
    },
    sectionNum: {
      type: Number,
      default: 0,
    },
    focus: {
      type: Number,
      default: 0,
    },
    editableQuesType: {
      type: Boolean,
      default: false,
    },
    attachTitle: {
      type: String,
      default: '',
    },
    textLabelOcap: {
      type: String,
      default: 'OCAP',
    },
    attachTitleOcap: {
      type: String,
      default() {
        return this.$t('OCAP附件');
      },
    },
    uploadFileOcapUrl: {
      type: String,
      default: '',
    },
    copyDropDown: {
      type: Boolean,
      default: false,
    },
    effectList: {
      type: Array,
    },
    uploadModule: {
      type: String,
    },
  },
  data() {
    return {
      isError: false,
      dropDown: false,
      editing: false,
      isLoading: {
        content: false,
      },
      errorMessage: [],
      inputQuestionTitle: '',
      inputQuestionContent: '',
      focusRadio: {
        buttonItems: [
          {
            id: 1,
            value: 'true',
            textLabel: this.$t('是'),
          },
          {
            id: 0,
            value: 'false',
            textLabel: this.$t('否'),
          },
        ],
      },
      quesTypeRadio: {
        buttonItems: [
          {
            id: 2,
            value: 'quality',
            textLabel: this.$t('質性'),
          },
          {
            id: 1,
            value: 'quantity',
            textLabel: this.$t('量性'),
          },
        ],
      },
      controlLimitCustomMessages: {
        customedMinValue: this.$t('管制界線下限不可小於合規界線'),
        customedMaxValue: this.$t('管制界線上限不可大於合規界線'),
        max_value: this.$t('管制界線下限不可大於管制界線上限'),
        min_value: this.$t('管制界線上限不可小於管制界線下限'),
      },
      specLimitCustomMessages: {
        customedMinValue: this.$t('合規界線下限不可大於管制界線'),
        customedMaxValue: this.$t('合規界線上限不可小於管制界線'),
        max_value: this.$t('合規界線下限不可大於合規界線上限'),
        min_value: this.$t('合規界線上限不可小於合規界線下限'),
      },
      hasFetch: false,
      effect: {
        createFields: Spare_factoryEffect.createFields,
        updateFields: Spare_factoryEffect.updateFields,
        deleteText: '',
      },
    };
  },
  computed: {
    _effectScope() {
      return {
        isCreatable: S_scope.$_isAvailable(['factory-effect-create']),
        isUpdatable: S_scope.$_isAvailable(['factory-effect-update']),
        isDeletable: S_scope.$_isAvailable(['factory-effect-delete']),
      };
    },
    _effectCreateUrl() {
      return S_factoryEffect.createUrl();
    },
    _isImageViewable() {
      return this.$store.state.app.factory.isImageViewable;
    },
  },
  watch: {
    value: {
      immediate: true,
      deep: true,
      handler() {
        this.hasFetch = this.value.hasFetch ? this.value.hasFetch : false;
      },
    },
  },
  mounted() {
    this.focusRadio.selected_condition = this.focus;
  },
  methods: {
    dropDownToggle() {
      if (this.editable) {
        this.dropDown = !this.dropDown;
      }
      if (this.dropDown && !this.hasFetch) {
        //when question is dropdown,fetch question content
        this.$emit('fetch-question');
        this.hasFetch = true;
      }
    },
    dropDownOpen() {
      if (this.copyDropDown) {
        this.dropDown = true;
      }
    },
    editClose() {
      this.editing = false;
    },
    $_onDeleteFile(index, key) {
      const value = this.value[key].filter((item, itemKey) => itemKey !== index);
      this.$emit('input', { key, value });
    },
    previewFile() {
      this.$emit('previewFile');
    },
    titleLength(text) {
      if (!text) return 0;
      return text.length;
    },
    deleteQues() {
      this.$emit('delete');
    },
    copyQuesClick() {
      this.$emit('copy-click');
    },
    async $_submitClick() {
      const isValidate = await this.$refs.formObserver.validate();
      if (!isValidate) {
        this.errorMessage = [`${this.$t('必填資料尚未填寫完整')}`];
      } else {
        this.errorMessage = [];
      }
    },
    $_isError($event) {
      this.isError = $event;
      if (this.isError) {
        this.errorMessage = [`${this.$t('必填資料尚未填寫完整')}`];
      } else {
        this.errorMessage = [];
      }
    },
    $_loadingStart() {
      this.isLoading.content = true;
    },
    $_loadingEnd() {
      this.isLoading.content = false;
    },
    $_onGoToFile($event) {
      H_file.goToFile($event);
    },
    $_onInput($event, key) {
      let value = $event;
      if (key === 'attaches' || key === 'ocapAttaches') value = [...this.value[key], ...$event];
      this.$emit('input', { key, value });
    },
  },
};
</script>
<style lang="scss">
@import '@/assets/scss/component/editable-checklist-question';
</style>
